import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import { of } from 'rxjs/observable/of';
import { catchError } from 'rxjs/operators';
import * as url from 'url';

import { Constants } from './const';
import { SessionStorageService } from './sessionstorage.service';

export interface CmsApiErrorResponse {
  HttpCode: number;
  Message: string;
}

export type CmsApiResponse = {} | CmsApiErrorResponse;

const tandcResponseEnglish: CmsApiResponse = {
  windowTitle: 'Terms and Conditions',
  heading1: 'Terms and Conditions',
  para1: 'Please select the Terms and Conditions of the Service Plan you are interested in below.',
  types: [
    {
      label: 'Care For My Home 360<br />Total Plan',
      image: 'total.svg',
      imageAlt: '',
      gotoLabel: 'Open',
      gotoAriaLabel: 'Open Terms and Conditions for Total Plan',
      targetUrl: 'total'
    },
    {
      label: 'Care For My Home 360<br />Appliance Plan',
      image: 'appliance.svg',
      imageAlt: '',
      gotoLabel: 'Open',
      gotoAriaLabel: 'Open Terms and Conditions for Appliance Plan',
      targetUrl: 'appliances'
    },
    {
      label: 'Care For My Home 360<br />System Plan',
      image: 'system.svg',
      imageAlt: '',
      gotoLabel: 'Open',
      gotoAriaLabel: 'Open Terms and Conditions for System Plan',
      targetUrl: 'systems'
    }
  ]
};

const tandcResponseFrench: CmsApiResponse = {
  windowTitle: 'Conditions générales',
  heading1: 'Conditions générales',
  para1: 'Veuillez sélectionner les conditions générales du plan de service qui vous intéresse.',
  types: [
    {
      label: 'Vigie Domicle 360<br />Plan total',
      image: 'total.svg',
      imageAlt: '',
      gotoLabel: 'Ouvrir',
      gotoAriaLabel: 'Ouvrir les conditions générales pour le plan complet',
      targetUrl: 'complet'
    },
    {
      label: 'Vigie Domicle 360<br />Plan appareils électroménagers',
      image: 'appliance.svg',
      imageAlt: '',
      gotoLabel: 'Ouvrir',
      gotoAriaLabel: 'Ouvrir les conditions générales pour le plan appareils électroménagers',
      targetUrl: 'appareilsem'
    },
    {
      label: 'Vigie Domicle 360<br />Plan systèmes',
      image: 'system.svg',
      imageAlt: '',
      gotoLabel: 'Ouvrir',
      gotoAriaLabel: 'Ouvrir les conditions générales pour le plan systèmes',
      targetUrl: 'systemes'
    }
  ]
};

@Injectable()
export class CmsApiService {
  // API endpoints relative to the base URL
  static readonly UrlPublicResources = 'public';
  static readonly UrlPublicHomeResources = 'public/home';
  static readonly UrlProductPlansResources = 'public/productplans';
  static readonly UrlLoginResources = 'public/login';
  static readonly UrlActivateResources = 'public/activateaccount';
  static readonly UrlForgotPasswordResources = 'public/forgotpswd';
  static readonly UrlPrivacyPolicyResources = 'public/privacypolicy';
  static readonly UrlPublicFaqResources = 'public/faq';
  static readonly UrlTotalTermsAndConditions = 'public/tc';
  static readonly UrlTotalTermsAndConditionsResources = 'public/tc/total';
  static readonly UrlSystemsTermsAndConditionsResources = 'public/tc/systems';
  static readonly UrlAppliancesTermsAndConditionsResources = 'public/tc/appliances';
  static readonly UrlContactUsResources = 'public/contactus';
  static readonly UrlHttp404Resources = 'public/http404';

  static readonly UrlDashResources = 'dash';
  static readonly UrlDashHomeResources = 'dash/home';
  static readonly UrlDashCallbackResources = 'dash/callback';
  static readonly UrlHomeSystemsResources = 'heatingcooling';
  static readonly UrlAppliancesResources = 'appliances';
  static readonly UrlHomeAssistanceResources = 'maintenance';
  static readonly UrlDashProfileResources = 'dash/profile';
  static readonly UrlDashReimbursementsResources = 'dash/reimbursements';
  static readonly UrlDashHomeInspectionResources = 'dash/reimbursements/homeinspection';
  static readonly UrlDashServiceGuaranteeResources = 'dash/reimbursements/serviceguarantee';
  static readonly UrlDashCashBackResources = 'dash/reimbursements/cashback';
  static readonly UrlDashFaqResources = 'dash/faq';

  // standard HTTP header names
  static readonly HeaderAccept = 'Accept';
  static readonly HeaderAcceptLanguage = 'Accept-Language';

  // default request header values
  static readonly DefaultAccept = 'application/json';

  // default request headers
  static readonly DefaultRequestHeaders = {
    [CmsApiService.HeaderAccept]: CmsApiService.DefaultAccept,
    [CmsApiService.HeaderAcceptLanguage]: Constants.DefaultLanguageCode
  };

  readonly getPublicResources = CmsApiService.createGetter(CmsApiService.UrlPublicResources);
  readonly getPublicHomeResources = CmsApiService.createGetter(CmsApiService.UrlPublicHomeResources);
  readonly getProductPlansResources = CmsApiService.createGetter(CmsApiService.UrlProductPlansResources);
  readonly getLoginResources = CmsApiService.createGetter(CmsApiService.UrlLoginResources);
  readonly getActivateResources = CmsApiService.createGetter(CmsApiService.UrlActivateResources);
  readonly getForgotPasswordResources = CmsApiService.createGetter(CmsApiService.UrlForgotPasswordResources);
  readonly getPrivacyPolicyResources = CmsApiService.createGetter(CmsApiService.UrlPrivacyPolicyResources);
  readonly getPublicFaqResources = CmsApiService.createGetter(CmsApiService.UrlPublicFaqResources);
  // tslint:disable:max-line-length
  readonly getTotalTermsAndConditionsResources = CmsApiService.createGetter(
    CmsApiService.UrlTotalTermsAndConditionsResources
  );
  readonly getSystemsTermsAndConditionsResources = CmsApiService.createGetter(
    CmsApiService.UrlSystemsTermsAndConditionsResources
  );
  readonly getAppliancesTermsAndConditionsResources = CmsApiService.createGetter(
    CmsApiService.UrlAppliancesTermsAndConditionsResources
  );
  // tslint:enable:max-line-length
  readonly getContactUsResources = CmsApiService.createGetter(CmsApiService.UrlContactUsResources);
  readonly getHttp404Resources = CmsApiService.createGetter(CmsApiService.UrlHttp404Resources);

  readonly getDashResources = CmsApiService.createGetter(CmsApiService.UrlDashResources);
  readonly getDashHomeResources = CmsApiService.createGetter(CmsApiService.UrlDashHomeResources);
  readonly getDashCallbackResources = CmsApiService.createGetter(CmsApiService.UrlDashCallbackResources);
  readonly getHomeSystemsResources = CmsApiService.createGetter(CmsApiService.UrlHomeSystemsResources);
  readonly getAppliancesResources = CmsApiService.createGetter(CmsApiService.UrlAppliancesResources);
  readonly getHomeAssistanceResources = CmsApiService.createGetter(CmsApiService.UrlHomeAssistanceResources);
  readonly getDashProfileResources = CmsApiService.createGetter(CmsApiService.UrlDashProfileResources);
  readonly getDashReimbursementsResources = CmsApiService.createGetter(CmsApiService.UrlDashReimbursementsResources);
  readonly getDashHomeInspectionResources = CmsApiService.createGetter(CmsApiService.UrlDashHomeInspectionResources);
  // tslint:disable-next-line:max-line-length
  readonly getDashServiceGuaranteeResources = CmsApiService.createGetter(
    CmsApiService.UrlDashServiceGuaranteeResources
  );
  readonly getDashCashBackResources = CmsApiService.createGetter(CmsApiService.UrlDashCashBackResources);
  readonly getDashFaqResources = CmsApiService.createGetter(CmsApiService.UrlDashFaqResources);

  static isErrorResponse(response: any): response is CmsApiErrorResponse {
    return (
      typeof response === 'object' && typeof response.HttpCode === 'number' && typeof response.Message === 'string'
    );
  }

  static resolveUrl(relativeUrl: string): string {
    return url.resolve(Constants.CmsApiBaseUrl, relativeUrl);
  }

  private static createGetter(relativeUrl: string): () => Observable<CmsApiResponse> {
    return function(): Observable<CmsApiResponse> {
      return this.get(relativeUrl);
    };
  }

  constructor(private httpClient: HttpClient, private sessionState: SessionStorageService) {
    this.handleError = this.handleError.bind(this);
  }

  private handleError(errorResponse: HttpErrorResponse): Observable<CmsApiErrorResponse> {
    const error = errorResponse.error;
    const errorMessage: string = error.message || error.Message || Constants.getGenericErrorMessage();
    return of(<CmsApiErrorResponse>{ Message: errorMessage, HttpCode: error.status });
  }

  getDefaultRequestHeaders(): HttpHeaders {
    return new HttpHeaders(
      Object.assign({}, CmsApiService.DefaultRequestHeaders, {
        [CmsApiService.HeaderAcceptLanguage]: this.sessionState.getCurrentLanguageCode()
      })
    );
  }

  get(relativeUrl: string, headers?: HttpHeaders): Observable<CmsApiResponse> {
    const apiUrl = CmsApiService.resolveUrl(relativeUrl);
    return this.httpClient
      .get<{}>(apiUrl, {
        headers: headers || this.getDefaultRequestHeaders(),
        observe: 'response'
      })
      .pipe(catchError(this.handleError))
      .map(
        (response: HttpResponse<{}> | CmsApiErrorResponse): CmsApiResponse => {
          return CmsApiService.isErrorResponse(response) ? response : response.body;
        }
      );
  }

  getTermsAndConditions(): Observable<CmsApiResponse> {
    const languageCode = this.sessionState.getCurrentLanguageCode();
    return of(languageCode === Constants.LanguageCodeFrench ? tandcResponseFrench : tandcResponseEnglish);
  }
}



// WEBPACK FOOTER //
// ./src/app/_services/cms-api.service.ts